import {
  AccountTree,
  AssignmentOutlined,
  AutorenewOutlined,
  CorporateFare,
  ErrorOutline,
  FolderOpen,
  HourglassEmpty,
  InsertChartOutlined,
  MonetizationOnOutlined,
  NotificationsOutlined,
  ReceiptLongOutlined,
  RequestQuoteOutlined,
  FindInPageOutlined,
  ScheduleOutlined,
  ShoppingCartOutlined,
} from '@mui/icons-material'
import { Roles } from '@omnea-digital/toolbox'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SidebarBackLink } from '@omnea-app/fe-app-shell/sidebar/SidebarBackLink'
import { SidebarUncontrolled } from '@omnea-app/fe-app-shell/sidebar/SidebarUncontrolled'
import { useSidebarMenuItems, type NavItem } from '@omnea-app/fe-app-shell/sidebar/useSidebarMenuItems'
import { useAssessmentsActionNeededCount } from '@omnea-app/fe-assessments/useAssessmentsActionNeededCount'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useRenewalsActionNeededCount } from '@omnea-app/fe-renewals/useRenewalsActionNeededCount'
import { useGetMyTaskCount } from '@omnea-app/fe-tasks/useTasks'
import { AppSidebarContent } from './AppSidebarContent'
import { useOriginalPath } from './hooks/useOriginalPath'
import { SettingsSidebarContent } from './settings/SettingsSidebarContent'

type NavItemCount = 'assessments' | 'renewals'

const NAV_ITEMS: readonly NavItem<NavItemCount>[] = [
  { route: 'requestsList', labelKey: 'sidebar.resources-requests', Icon: HourglassEmpty },
  { route: 'suppliersList', labelKey: 'sidebar.resources-suppliers', Icon: CorporateFare },
  { route: 'assessmentsList', labelKey: 'sidebar.resources-assessments', Icon: AssignmentOutlined, count: 'assessments' },
  { route: 'agreementsList', labelKey: 'sidebar.resources-agreements', Icon: RequestQuoteOutlined },
  {
    route: 'purchaseOrdersList',
    labelKey: 'sidebar.resources-purchase-orders',
    Icon: ShoppingCartOutlined,
    badgeKey: 'sidebar.new-badge',
  },
  { route: 'renewalsList', labelKey: 'sidebar.resources-renewals', Icon: AutorenewOutlined, count: 'renewals' },
  { route: 'scheduledRequestsList', labelKey: 'sidebar.resources-scheduled-requests', Icon: ScheduleOutlined },
  {
    route: 'insights',
    labelKey: 'sidebar.resources-insights',
    Icon: InsertChartOutlined,
    // `/reporting` is the same product under its former address.
    activePaths: ['/insights', '/reporting'],
  },
  {
    labelKey: 'sidebar.resources-risk-management',
    Icon: ErrorOutline,
    children: [
      { route: 'risksList', labelKey: 'sidebar.resources-risks', Icon: ErrorOutline },
      { route: 'riskAlertsList', labelKey: 'sidebar.resources-alerts', Icon: NotificationsOutlined },
      { route: 'subprocessorsList', labelKey: 'sidebar.resources-subprocessors', Icon: AccountTree },
    ],
  },
  { route: 'documentsList', labelKey: 'sidebar.resources-documents', Icon: FolderOpen },
  { route: 'budgetsList', labelKey: 'sidebar.resources-budgets', Icon: MonetizationOnOutlined },
  { route: 'billsList', labelKey: 'sidebar.bills', Icon: ReceiptLongOutlined, badgeKey: 'sidebar.new-badge' },
  { route: 'productCatalogues', labelKey: 'sidebar.resources-product-catalogues', Icon: ShoppingCartOutlined },
  { route: 'sourcingDashboard', labelKey: 'sidebar.resources-savings-dashboard', Icon: FindInPageOutlined },
]

type Props = {
  path: string | null
  sidebarCollapsedByDefault?: boolean
}

export function Sidebar({ path, sidebarCollapsedByDefault }: Props) {
  const { t } = useTranslation('common')
  const { role } = useAuthenticatedUser().user
  const isSettingsDashboard = Boolean(path?.includes('/settings'))
  const backToAppPath = useOriginalPath(isSettingsDashboard)
  const isExpandedExternal = sidebarCollapsedByDefault !== true

  const { data: numAssessmentsActionNeeded = null } = useAssessmentsActionNeededCount()
  const { data: numRenewalsActionNeeded = null } = useRenewalsActionNeededCount()
  const { data: taskCount = 0 } = useGetMyTaskCount()

  const [isExpanded, toggleExpanded] = useState(isExpandedExternal)

  useEffect(() => {
    toggleExpanded(isExpandedExternal)
  }, [isExpandedExternal])

  const countsByName: Readonly<Record<NavItemCount, number | null>> = {
    assessments: numAssessmentsActionNeeded,
    renewals: numRenewalsActionNeeded,
  }

  const items = useSidebarMenuItems(NAV_ITEMS, countsByName)

  if (role === Roles.SUPPLIER) {
    return null
  }

  return (
    <SidebarUncontrolled
      // The settings header has no collapse toggle, so a collapsed sidebar there could not be reopened.
      isSidebarExpanded={isExpanded || isSettingsDashboard}
      onToggleExpanded={toggleExpanded}
      header={isSettingsDashboard ? <SidebarBackLink href={backToAppPath} label={t('sidebar.back-to-app')} /> : undefined}
    >
      {isSettingsDashboard ? (
        <SettingsSidebarContent />
      ) : (
        <AppSidebarContent isSidebarExpanded={isExpanded} items={items} numberOfActiveTasks={taskCount} />
      )}
    </SidebarUncontrolled>
  )
}
